import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Guides, type Guide, type GuideCategory } from '@/lib/api/v1';
import { Badge, Card, EmptyState, Icon, Skeleton, type IconName } from '@/design-system';
import { GuideCover, guideCategoryLabel, guideDifficultyLabel, guideTimeLabel } from '@/components/GuideCover';

/**
 * Guides — short how-to recipes that complement the cocktail engine.
 *
 * Categories drive both the filter pills and the card icons. Each guide
 * has totalMinutes, difficulty, and "unlocks" — the cocktails it makes
 * possible. This is what the brand promise calls "Guides — pick the right
 * ice, batch a syrup, dehydrate orange slices".
 */

const CATEGORY_PILLS: { value: '' | GuideCategory; label: string; icon: IconName }[] = [
  { value: '', label: 'Alle', icon: 'list' },
  { value: 'syrup', label: 'Sirup', icon: 'sparkles' },
  { value: 'garnish', label: 'Pynt', icon: 'cherry' },
  { value: 'ice', label: 'Is', icon: 'snow' },
  { value: 'technique', label: 'Teknikk', icon: 'chef-hat' },
  { value: 'prep', label: 'Forberedelser', icon: 'container' },
  { value: 'sober-curious', label: 'Lavalkohol', icon: 'heart' },
];

export function GuidesPage() {
  const [category, setCategory] = useState<'' | GuideCategory>('');

  const guidesQ = useQuery({
    queryKey: ['guides', category],
    queryFn: () =>
      Guides.list({
        category: (category || undefined) as GuideCategory | undefined,
      }),
    staleTime: 10 * 60_000,
  });

  const guides = guidesQ.data?.guides ?? [];

  return (
    <div className="grid gap-6">
      <header className="grid gap-2">
        <h1 className="ui-section-title text-3xl">Guides</h1>
        <p className="text-sm text-mist">
          Artikler for bedre hjemmebar: sirup, is, pynt, teknikk og forberedelser som faktisk merkes i glasset.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {CATEGORY_PILLS.map((c) => (
          <button
            key={c.value}
            type="button"
            onClick={() => setCategory(c.value)}
            className={`ui-pill ${category === c.value ? 'ui-pill--active' : ''}`}
          >
            <Icon name={c.icon} size={12} />
            {c.label}
          </button>
        ))}
      </div>

      {guidesQ.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="grid gap-3 p-3">
              <Skeleton height={192} rounded="md" />
              <Skeleton height={22} width="80%" />
              <Skeleton height={14} />
              <Skeleton height={64} />
            </Card>
          ))}
        </div>
      ) : guides.length === 0 ? (
        <EmptyState icon="list" title="Ingen guides her" description="Bytt kategori." />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {guides.map((g) => (
            <GuideCard key={g.slug} guide={g} />
          ))}
        </div>
      )}
    </div>
  );
}

function GuideCard({ guide }: { guide: Guide }) {
  const time = guideTimeLabel(guide.totalMinutes);
  const difficulty = guideDifficultyLabel(guide.difficulty);
  const previewSteps = guide.steps.slice(0, 2);
  const ingredientPreview = guide.ingredients.slice(0, 3).map((ingredient) => ingredient.name);

  return (
    <Link
      to={`/guides/${guide.slug}`}
      className="ui-card ui-card--interactive grid min-h-full gap-0 overflow-hidden p-0"
    >
      <GuideCover category={guide.category} title={guide.title} />

      <div className="grid gap-3 p-4">
        <div className="flex flex-wrap gap-1.5">
          <Badge tone="gold">{time}</Badge>
          <Badge tone="mist">{difficulty}</Badge>
          <Badge tone="mist">{guideCategoryLabel(guide.category)}</Badge>
        </div>

        <div className="grid gap-2">
          <p className="text-sm leading-relaxed text-parchment line-clamp-3">{guide.description}</p>
          {(guide.yields || guide.keepsFor || ingredientPreview.length > 0) && (
            <p className="text-xs leading-relaxed text-mist line-clamp-2">
              {[guide.yields, guide.keepsFor, ingredientPreview.join(', ')].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>

        {previewSteps.length > 0 && (
          <div className="grid gap-1.5 border-t border-line pt-3">
            <span className="text-[10px] font-semibold uppercase tracking-widest text-mist">I artikkelen</span>
            {previewSteps.map((step, idx) => (
              <p key={idx} className="text-xs leading-relaxed text-mist line-clamp-2">
                <span className="text-candle">{idx + 1}.</span> {step.title ? `${step.title}: ` : ''}
                {step.body}
              </p>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-1">
          {guide.tags.slice(0, 3).map((t) => (
            <Badge key={t} tone="mist">{t}</Badge>
          ))}
          {guide.unlocks.length > 0 && (
            <Badge tone="gold">låser opp {guide.unlocks.length}</Badge>
          )}
        </div>
      </div>
    </Link>
  );
}
